import { useMemo, useState } from 'react';
import type { ClassworkAssignment } from '../../lib/classworkApi';

type Props = {
  assignments: ClassworkAssignment[];
  classworkId?: number;
  latestSubmissionByAssignmentId?: Record<number, any>;
  archivedSubmissionsByAssignmentId?: Record<number, any[]>;
  /** Neueste classwork_grades-Zeile pro assignment_id (z. B. nach LLM-Auto-Note) */
  gradeRowByAssignmentId?: Record<number, any>;
  /** Neueste classwork_corrections-Zeile pro assignment_id (Status aus Detail-API) */
  latestCorrectionByAssignmentId?: Record<number, { id?: number; status?: string; pipeline_status?: string | null }>;
  correctionRunningByAssignmentId?: Record<number, boolean>;
  /** Live Stufen-Text (SSE) — ergänzt pipeline_status aus der API */
  correctionPipelineLabelByAssignmentId?: Record<number, string>;
  onlyofficeWarningsByAssignmentId?: Record<number, string>;
  onQueueCorrection: (assignmentId: number) => void;
  /** KI-Ergebnis anzeigen (ohne neuen Lauf) */
  onViewCorrectionResult?: (assignmentId: number) => void;
  /** Pipeline-Überprüfung (Regions/Crops) öffnen */
  onOpenPipelineReview?: (assignmentId: number) => void;
  onArchiveSubmission?: (assignmentId: number) => void;
  onRestoreSubmission?: (assignmentId: number, submissionId: number) => void;
  onPublish: (assignmentId: number, options?: { revoke?: boolean }) => void;
  onRetryOnlyoffice?: (assignmentId: number) => void;
};

type SubmissionStatus = 'keine_abgabe' | 'in_bearbeitung' | 'abgegeben' | 'abgelaufen';

export default function ClassworkSubmissionBoard({
  assignments,
  classworkId,
  latestSubmissionByAssignmentId = {},
  archivedSubmissionsByAssignmentId = {},
  gradeRowByAssignmentId = {},
  latestCorrectionByAssignmentId = {},
  correctionRunningByAssignmentId = {},
  correctionPipelineLabelByAssignmentId = {},
  onlyofficeWarningsByAssignmentId = {},
  onQueueCorrection,
  onViewCorrectionResult,
  onOpenPipelineReview,
  onArchiveSubmission,
  onRestoreSubmission,
  onPublish,
  onRetryOnlyoffice
}: Props) {
  const [sortBy, setSortBy] = useState<'class' | 'last' | 'first' | 'unid' | 'status'>('class');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [showBoardOptions, setShowBoardOptions] = useState(false);

  const sortedAssignments = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const list = [...assignments];
    list.sort((a, b) => {
      const aClass = String(a.class_name || '').toLowerCase();
      const bClass = String(b.class_name || '').toLowerCase();
      const aLast = String(a.last_name || '').toLowerCase();
      const bLast = String(b.last_name || '').toLowerCase();
      const aFirst = String(a.first_name || '').toLowerCase();
      const bFirst = String(b.first_name || '').toLowerCase();
      const aUnid = String(a.student_id || '').toLowerCase();
      const bUnid = String(b.student_id || '').toLowerCase();
      const aState = String(a.state || '').toLowerCase();
      const bState = String(b.state || '').toLowerCase();
      const pick = (x: string, y: string) => (x < y ? -1 : x > y ? 1 : 0);

      let cmp = 0;
      if (sortBy === 'class') cmp = pick(aClass, bClass) || pick(aLast, bLast) || pick(aFirst, bFirst) || pick(aUnid, bUnid);
      if (sortBy === 'last') cmp = pick(aLast, bLast) || pick(aFirst, bFirst) || pick(aUnid, bUnid);
      if (sortBy === 'first') cmp = pick(aFirst, bFirst) || pick(aLast, bLast) || pick(aUnid, bUnid);
      if (sortBy === 'unid') cmp = pick(aUnid, bUnid);
      if (sortBy === 'status') cmp = pick(aState, bState) || pick(aLast, bLast) || pick(aFirst, bFirst) || pick(aUnid, bUnid);
      return cmp * dir;
    });
    return list;
  }, [assignments, sortBy, sortDir]);

  const resolveAssignmentId = (assignment: ClassworkAssignment): number => {
    const raw = (assignment as any)?.id ?? (assignment as any)?.assignment_id;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };

  const clampPercent = (value: unknown): number | null => {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return Math.max(0, Math.min(100, Math.round(n)));
  };

  const resolveAnalysisProgress = (submission: any): number | null => {
    if (!submission) return null;
    const directCandidates = [
      submission.analysis_progress,
      submission.analysisProgress,
      submission.progress_percent,
      submission.progress
    ];
    for (const c of directCandidates) {
      const p = clampPercent(c);
      if (p != null) return p;
    }
    let meta: any = submission.metadata;
    if (typeof meta === 'string') {
      try {
        meta = JSON.parse(meta);
      } catch {
        meta = null;
      }
    }
    if (meta && typeof meta === 'object') {
      const metaCandidates = [
        meta.analysis_progress,
        meta.analysisProgress,
        meta?.analysis?.progress,
        meta?.analysis?.progress_percent,
        meta?.vision?.progress,
        meta?.ocr?.progress
      ];
      for (const c of metaCandidates) {
        const p = clampPercent(c);
        if (p != null) return p;
      }
    }
    const analysisStatus = String(submission?.analysis_status || '').toLowerCase().trim();
    const ocrStatus = String(submission?.ocr_status || '').toLowerCase().trim();
    if (analysisStatus === 'completed') return 100;
    if (analysisStatus === 'failed') return 0;
    if (analysisStatus === 'running') return ocrStatus === 'completed' ? 70 : 55;
    if (analysisStatus === 'pending') {
      if (ocrStatus === 'completed') return 45;
      if (ocrStatus === 'pending') return 20;
      return 30;
    }
    return null;
  };

  type StatusTone =
    | 'nicht_begonnen'
    | 'in_bearbeitung'
    | 'abgegeben'
    | 'analyse_laeuft'
    | 'bewertet'
    | 'veroeffentlicht'
    | 'abgelaufen';

  function assignmentStatusTone(
    stateRaw: string | null | undefined,
    analysisStatusRaw: string | null | undefined,
    hasSubmission: boolean,
    latestCorrectionStatus: string | null | undefined,
    gradeRow: any | null | undefined
  ): StatusTone {
    const s = String(stateRaw || '').toLowerCase().trim();
    const analysis = String(analysisStatusRaw || '').toLowerCase().trim();
    const corrSt = String(latestCorrectionStatus || '').toLowerCase().trim();
    const gStatus = String(gradeRow?.status || '').toLowerCase().trim();
    const hasFinalGradeWithCorrection =
      Boolean(gradeRow?.correction_id) && (gStatus === 'final' || gStatus === 'published');
    if (s === 'published') return 'veroeffentlicht';
    if (s === 'graded') return 'bewertet';
    if (hasFinalGradeWithCorrection || corrSt === 'completed') return 'bewertet';
    if (hasSubmission || s === 'submitted' || s === 'reviewed' || s === 'locked') {
      if (analysis === 'pending' || analysis === 'running') return 'analyse_laeuft';
      return 'abgegeben';
    }
    if (s === 'opened' || s === 'in_progress' || s === 'reopened') return 'in_bearbeitung';
    if (s === 'expired') return 'abgelaufen';
    return 'nicht_begonnen';
  }

  const toneRowClass: Record<StatusTone, string> = {
    nicht_begonnen: 'border-slate-300 bg-slate-50/90 hover:bg-slate-100/90',
    in_bearbeitung: 'border-amber-300 bg-amber-50/90 hover:bg-amber-100/80',
    abgegeben: 'border-amber-300 bg-amber-50/90 hover:bg-amber-100/80',
    analyse_laeuft: 'border-blue-300 bg-blue-50/90 hover:bg-blue-100/80',
    bewertet: 'border-indigo-300 bg-indigo-50/90 hover:bg-indigo-100/80',
    veroeffentlicht: 'border-emerald-300 bg-emerald-50/90 hover:bg-emerald-100/80',
    abgelaufen: 'border-rose-300 bg-rose-50/90 hover:bg-rose-100/80'
  };

  const toneBadgeClass: Record<StatusTone, string> = {
    nicht_begonnen: 'bg-slate-200 text-slate-900 border-slate-300',
    in_bearbeitung: 'bg-amber-100 text-amber-950 border-amber-300',
    abgegeben: 'bg-amber-100 text-amber-950 border-amber-300',
    analyse_laeuft: 'bg-blue-100 text-blue-900 border-blue-300',
    bewertet: 'bg-indigo-100 text-indigo-950 border-indigo-300',
    veroeffentlicht: 'bg-emerald-100 text-emerald-950 border-emerald-300',
    abgelaufen: 'bg-rose-100 text-rose-950 border-rose-300'
  };

  const toneLabel: Record<StatusTone, string> = {
    nicht_begonnen: 'Noch nicht begonnen',
    in_bearbeitung: 'In Bearbeitung',
    abgegeben: 'Abgegeben',
    analyse_laeuft: 'Wird analysiert',
    bewertet: 'Bewertet',
    veroeffentlicht: 'Veröffentlicht',
    abgelaufen: 'Abgelaufen'
  };

  const statusBadgeClass = {
    neutral: 'bg-slate-100 text-slate-800 border-slate-200',
    warning: 'bg-amber-100 text-amber-900 border-amber-300',
    info: 'bg-blue-100 text-blue-900 border-blue-300',
    success: 'bg-emerald-100 text-emerald-900 border-emerald-300',
    accent: 'bg-indigo-100 text-indigo-900 border-indigo-300',
    danger: 'bg-rose-100 text-rose-900 border-rose-300'
  } as const;

  const submissionStateUi = (stateRaw: string | null | undefined, hasSubmission: boolean): { label: string; className: string } => {
    const s = String(stateRaw || '').toLowerCase().trim();
    let status: SubmissionStatus = 'keine_abgabe';
    if (s === 'expired') status = 'abgelaufen';
    else if (hasSubmission || s === 'submitted' || s === 'reviewed' || s === 'locked' || s === 'graded' || s === 'published') status = 'abgegeben';
    else if (s === 'opened' || s === 'in_progress' || s === 'reopened') status = 'in_bearbeitung';
    if (status === 'abgegeben') return { label: 'Abgegeben', className: statusBadgeClass.warning };
    if (status === 'in_bearbeitung') return { label: 'In Bearbeitung', className: statusBadgeClass.warning };
    if (status === 'abgelaufen') return { label: 'Abgelaufen', className: statusBadgeClass.danger };
    return { label: 'Noch keine Abgabe', className: statusBadgeClass.neutral };
  };

  const correctionStateUi = (
    stateRaw: string | null | undefined,
    analysisStatusRaw: string | null | undefined,
    hasSubmission: boolean,
    latestCorrectionStatus: string | null | undefined,
    isCorrectionJobRunning: boolean,
    pipelineStatusLabel: string | null | undefined
  ): { label: string; className: string } => {
    const s = String(stateRaw || '').toLowerCase().trim();
    const analysis = String(analysisStatusRaw || '').toLowerCase().trim();
    const corr = String(latestCorrectionStatus || '').toLowerCase().trim();
    const pipe = String(pipelineStatusLabel || '').trim();

    if (s === 'published') return { label: 'Veröffentlicht', className: statusBadgeClass.success };
    if (s === 'graded') return { label: 'Bewertet', className: statusBadgeClass.accent };

    if (isCorrectionJobRunning || corr === 'queued' || corr === 'processing') {
      return { label: pipe || 'Korrektur läuft', className: statusBadgeClass.info };
    }
    if (corr === 'failed') {
      return { label: 'Korrektur fehlgeschlagen', className: statusBadgeClass.danger };
    }
    if (corr === 'completed') {
      return { label: 'Korrektur fertig', className: statusBadgeClass.accent };
    }

    if (s === 'expired') return { label: 'Abgelaufen', className: statusBadgeClass.danger };
    if (!hasSubmission) return { label: 'Wartet auf Abgabe', className: statusBadgeClass.neutral };
    if (analysis === 'pending' || analysis === 'running') {
      return { label: 'Analyse läuft', className: statusBadgeClass.info };
    }
    if (analysis === 'completed' || analysis === 'failed') {
      return { label: 'Bereit zur Korrektur', className: statusBadgeClass.warning };
    }
    return { label: 'Kein Korrekturlauf', className: statusBadgeClass.neutral };
  };

  const pendingAnalysisCount = useMemo(
    () =>
      sortedAssignments.filter((assignment) => {
        const assignmentId = resolveAssignmentId(assignment);
        const latestSubmission = assignmentId ? latestSubmissionByAssignmentId[assignmentId] : null;
        const analysisStatus = String(latestSubmission?.analysis_status || '').toLowerCase();
        return analysisStatus === 'pending' || analysisStatus === 'running';
      }).length,
    [latestSubmissionByAssignmentId, sortedAssignments]
  );

  const workflowCounts = useMemo(() => {
    let waitingForSubmission = 0;
    let readyForCorrection = 0;
    let readyForPublish = 0;
    for (const assignment of sortedAssignments) {
      const assignmentId = resolveAssignmentId(assignment);
      const latestSubmission = assignmentId ? latestSubmissionByAssignmentId[assignmentId] : null;
      const analysisStatus = String(latestSubmission?.analysis_status || '').toLowerCase();
      const hasSubmission = Boolean(latestSubmission);
      const canQueueCorrection = ['submitted', 'reviewed'].includes(String(assignment.state || '').toLowerCase());
      const analysisReady = hasSubmission ? ['completed', 'failed'].includes(analysisStatus) : false;
      const canPublish = ['graded', 'published'].includes(String(assignment.state || '').toLowerCase());
      if (!hasSubmission) waitingForSubmission += 1;
      if (canQueueCorrection && analysisReady) readyForCorrection += 1;
      if (canPublish) readyForPublish += 1;
    }
    return { waitingForSubmission, readyForCorrection, readyForPublish };
  }, [latestSubmissionByAssignmentId, sortedAssignments]);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-xl font-semibold text-gray-900">Abgaben & Status</h3>
        <button type="button" className="text-xs font-medium text-gray-700 underline" onClick={() => setShowBoardOptions((prev) => !prev)}>
          {showBoardOptions ? 'Ansichtsoptionen ausblenden' : 'Ansichtsoptionen anzeigen'}
        </button>
      </div>
      {pendingAnalysisCount > 0 ? (
        <div className="mb-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
          {pendingAnalysisCount} Abgabe(n) werden aktuell im Hintergrund analysiert.
        </div>
      ) : null}
      <div className="mb-3 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-950">
        <span className="font-semibold">Was jetzt tun?</span>{' '}
        {workflowCounts.readyForCorrection > 0 ? (
          <span>{workflowCounts.readyForCorrection}x „LLM-Korrektur“ starten. </span>
        ) : (
          <span>Aktuell keine Abgabe ist bereit für „LLM-Korrektur“. </span>
        )}
        {workflowCounts.readyForPublish > 0 ? (
          <span>Danach {workflowCounts.readyForPublish}x „N“ (veröffentlichen/widerrufen). </span>
        ) : null}
        {workflowCounts.waitingForSubmission > 0 ? (
          <span>{workflowCounts.waitingForSubmission} warten noch auf Schüler-Abgabe.</span>
        ) : null}
      </div>
      {showBoardOptions ? (
        <div className="mb-3 rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs text-gray-600">Sortieren nach</label>
            <select className="input-field !h-9 !py-1 !text-xs min-w-[150px]" value={sortBy} onChange={(e) => setSortBy(e.target.value as any)}>
              <option value="class">Klasse</option>
              <option value="last">Nachname</option>
              <option value="first">Vorname</option>
              <option value="unid">UNID</option>
              <option value="status">Status</option>
            </select>
            <button
              type="button"
              className="btn-secondary text-xs"
              onClick={() => setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
            >
              {sortDir === 'asc' ? 'A-Z' : 'Z-A'}
            </button>
          </div>
          {assignments.length > 0 ? (
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-gray-600">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 shrink-0 rounded-sm bg-slate-200 ring-1 ring-slate-400/60" />
                Noch nicht begonnen
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 shrink-0 rounded-sm bg-amber-200 ring-1 ring-amber-400/60" />
                In Bearbeitung
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 shrink-0 rounded-sm bg-amber-200 ring-1 ring-amber-400/60" />
                Abgegeben
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 shrink-0 rounded-sm bg-blue-200 ring-1 ring-blue-400/60" />
                Wird analysiert
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 shrink-0 rounded-sm bg-emerald-200 ring-1 ring-emerald-400/60" />
                Veröffentlicht
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 shrink-0 rounded-sm bg-rose-200 ring-1 ring-rose-400/60" />
                Abgelaufen
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
      {!assignments.length && <p className="text-sm text-gray-500">Noch keine Zuweisungen.</p>}
      <div className="space-y-2">
        {sortedAssignments.map((assignment) => {
          const assignmentId = resolveAssignmentId(assignment);
          const latestSubmission = assignmentId ? latestSubmissionByAssignmentId[assignmentId] : null;
          const archivedSubmissions = assignmentId ? archivedSubmissionsByAssignmentId[assignmentId] || [] : [];
          const gradeRow = assignmentId ? gradeRowByAssignmentId[assignmentId] : null;
          const gradeVal = String(gradeRow?.grade_value ?? '').trim();
          const gradePctRaw = gradeRow?.score_percent;
          const gradePct =
            gradePctRaw != null && gradePctRaw !== '' && !Number.isNaN(Number(gradePctRaw)) ? Number(gradePctRaw) : null;
          const showAutoGrade =
            gradeRow &&
            ((gradeVal && gradeVal.toLowerCase() !== 'n/a') || (gradePct != null && Number.isFinite(gradePct)));
          let submissionVia = String(latestSubmission?.source || '');
          if (latestSubmission?.metadata) {
            try {
              const meta = typeof latestSubmission.metadata === 'string'
                ? JSON.parse(latestSubmission.metadata)
                : latestSubmission.metadata;
              if (meta?.submitted_via) submissionVia = String(meta.submitted_via);
            } catch (_) {
              // ignore malformed metadata
            }
          }
          const canQueueCorrection = ['submitted', 'reviewed'].includes(String(assignment.state || ''));
          const canPublish = ['graded', 'published'].includes(String(assignment.state || '').toLowerCase());
          const canArchiveSubmission =
            Boolean(assignmentId && latestSubmission) &&
            ['submitted', 'reviewed', 'graded', 'published'].includes(String(assignment.state || '').toLowerCase()) &&
            ['submitted', 'locked'].includes(String(latestSubmission?.state || '').toLowerCase());
          const analysisStatus = String(latestSubmission?.analysis_status || '');
          let analysisPolicy: any = null;
          if (latestSubmission?.analysis_json) {
            try {
              analysisPolicy =
                typeof latestSubmission.analysis_json === 'string'
                  ? JSON.parse(latestSubmission.analysis_json)?.policyDecision || null
                  : latestSubmission.analysis_json?.policyDecision || null;
            } catch {
              analysisPolicy = null;
            }
          }
          const analysisReady = latestSubmission
            ? ['completed', 'failed'].includes(analysisStatus)
            : false;
          const analysisRunning = latestSubmission ? ['pending', 'running'].includes(String(analysisStatus).toLowerCase()) : false;
          const analysisProgress = resolveAnalysisProgress(latestSubmission);
          const isCorrectionRunning = Boolean(assignmentId && correctionRunningByAssignmentId[assignmentId]);
          const latestCorrRow = assignmentId ? latestCorrectionByAssignmentId[assignmentId] : undefined;
          const latestCorrStatusRaw = latestCorrRow?.status;
          const latestCorrStatus = String(latestCorrStatusRaw || '').toLowerCase();
          const serverPipeline = String(latestCorrRow?.pipeline_status || '').trim();
          const livePipeline = String((assignmentId && correctionPipelineLabelByAssignmentId[assignmentId]) || '').trim();
          const pipelineStatusLabel = livePipeline || serverPipeline || null;
          const hasCorrectionResult =
            Boolean(gradeRow?.correction_id) ||
            ['graded', 'published'].includes(String(assignment.state || '').toLowerCase()) ||
            latestCorrStatus === 'completed';
          const canOpenKiModal = Boolean(assignmentId && latestSubmission && analysisReady && hasCorrectionResult && !isCorrectionRunning);
          const canStartCorrection = Boolean(assignmentId && latestSubmission && canQueueCorrection && analysisReady && !isCorrectionRunning);
          const canAssignGrade = Boolean(assignmentId && canPublish);
          const isPublished = String(assignment.state || '').toLowerCase() === 'published' || String(gradeRow?.status || '').toLowerCase() === 'published';
          const tone = assignmentStatusTone(
            assignment.state,
            analysisStatus,
            Boolean(latestSubmission),
            latestCorrStatusRaw,
            gradeRow
          );
          const submissionUi = submissionStateUi(assignment.state, Boolean(latestSubmission));
          const correctionUi = correctionStateUi(
            assignment.state,
            analysisStatus,
            Boolean(latestSubmission),
            latestCorrStatusRaw,
            isCorrectionRunning,
            pipelineStatusLabel
          );
          return (
          <div
            key={assignmentId || assignment.student_id}
            className={`border-2 transition-all rounded-lg p-3 flex items-center justify-between gap-3 ${toneRowClass[tone]}`}
          >
            <div className="min-w-0">
              <div className="font-medium text-sm">
                {assignment.last_name || assignment.first_name
                  ? `${assignment.last_name || ''}${assignment.last_name && assignment.first_name ? ', ' : ''}${assignment.first_name || ''}`
                  : assignment.student_id}
              </div>
              <div className="text-xs text-gray-500">
                {assignment.class_name || '-'} · {assignment.student_id}
              </div>
              <div className="text-xs mt-0.5">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span
                    title={`Abgabe-Status · System: ${String(assignment.state || '—')}`}
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${submissionUi.className}`}
                  >
                    Abgabe: {submissionUi.label}
                  </span>
                  <span
                    title={`Korrektur-Status · letzter Lauf: ${latestCorrStatusRaw || '—'} · Analyse: ${analysisStatus || '—'}`}
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${correctionUi.className}`}
                  >
                    Korrektur: {correctionUi.label}
                  </span>
                  <span
                    title={`System-Status: ${String(assignment.state || '—')}`}
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${toneBadgeClass[tone]}`}
                  >
                    Gesamt: {toneLabel[tone]}
                  </span>
                </div>
              </div>
              {latestSubmission ? (
                <div className="mt-1 text-xs text-gray-600">
                  Abgabe: <span className="font-medium text-gray-700">{latestSubmission.original_name || 'Dokument'}</span>
                  {submissionVia ? ` · ${submissionVia}` : ''}
                  {analysisStatus ? ` · Analyse: ${analysisStatus}` : ''}
                  {classworkId && latestSubmission.id ? (
                    <a
                      className="ml-2 text-primary-700 hover:underline"
                      href={`/api/classwork/${classworkId}/submissions/${latestSubmission.id}/view`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Öffnen
                    </a>
                  ) : null}
                </div>
              ) : (
                <div className="mt-1 text-xs text-gray-500">Abgabe: noch kein Dokument</div>
              )}
              {analysisPolicy ? (
                <div className="mt-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-800">
                  <span className="font-semibold">Warum diese Entscheidung?</span>{' '}
                  {String(analysisPolicy.decision || 'AUTO_READY')} ·{' '}
                  {String(analysisPolicy.reasonCode || 'OK')}
                  {analysisPolicy.confidence != null ? (
                    <span> · OCR {Math.round(Number(analysisPolicy.confidence) * 100)}%</span>
                  ) : null}
                  {analysisPolicy.alignmentConfidence != null ? (
                    <span> · Align {Math.round(Number(analysisPolicy.alignmentConfidence) * 100)}%</span>
                  ) : null}
                </div>
              ) : null}
              {archivedSubmissions.length ? (
                <details className="mt-1">
                  <summary className="cursor-pointer text-xs text-amber-700 hover:text-amber-900">
                    Archivierte Einreichungen ({archivedSubmissions.length})
                  </summary>
                  <div className="mt-1 space-y-1 rounded-md border border-amber-200 bg-amber-50/60 p-2">
                    {archivedSubmissions.map((sub, idx) => {
                      const subId = Number(sub?.id);
                      const tsRaw = sub?.updated_at || sub?.submitted_at || sub?.created_at;
                      const ts = tsRaw ? new Date(tsRaw).toLocaleString() : null;
                      return (
                        <div key={subId > 0 ? subId : `${assignmentId}-archived-${idx}`} className="flex flex-wrap items-center gap-2 text-[11px] text-amber-900">
                          <span className="font-medium">#{sub?.attempt_no || '—'}</span>
                          <span>{sub?.original_name || 'Dokument'}</span>
                          {ts ? <span className="text-amber-700">· {ts}</span> : null}
                          {classworkId && subId > 0 ? (
                            <a
                              className="text-primary-700 hover:underline"
                              href={`/api/classwork/${classworkId}/submissions/${subId}/view`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Öffnen
                            </a>
                          ) : null}
                          {onRestoreSubmission && assignmentId && subId > 0 ? (
                            <button
                              type="button"
                              className="rounded border border-amber-400 bg-white px-2 py-0.5 font-medium text-amber-900 hover:bg-amber-100"
                              onClick={() => onRestoreSubmission(assignmentId, subId)}
                            >
                              Wieder einsetzen
                            </button>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </details>
              ) : null}
              {analysisRunning ? (
                <div className="mt-2 rounded-md border border-blue-200 bg-blue-50 px-2 py-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="inline-flex items-center gap-1.5 text-[11px] font-medium text-blue-900">
                      <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-blue-300 border-t-blue-800 animate-spin" />
                      Vorbereitung läuft (OCR/Text/Bilder/Analyse)
                    </div>
                    <span className="text-[11px] font-semibold text-blue-900">
                      {analysisProgress != null ? `${analysisProgress}%` : 'läuft'}
                    </span>
                  </div>
                  {analysisProgress != null ? (
                    <div className="mt-1.5 h-1.5 w-full rounded-full bg-blue-100 overflow-hidden">
                      <div className="h-full bg-blue-500 transition-all duration-500" style={{ width: `${analysisProgress}%` }} />
                    </div>
                  ) : null}
                </div>
              ) : null}
              {showAutoGrade ? (
                <div className="mt-1 text-xs">
                  <span className="inline-flex flex-wrap items-center gap-x-1 rounded-md border border-indigo-200 bg-indigo-50/90 px-2 py-0.5 font-medium text-indigo-950">
                    <span className="text-indigo-700">Note (KI / Schema):</span>
                    {gradeVal && gradeVal.toLowerCase() !== 'n/a' ? <span>{gradeVal}</span> : null}
                    {gradeVal && gradeVal.toLowerCase() !== 'n/a' && gradePct != null ? <span>·</span> : null}
                    {gradePct != null ? <span>{Math.round(gradePct)} %</span> : null}
                  </span>
                </div>
              ) : null}
              {assignmentId && onlyofficeWarningsByAssignmentId[assignmentId] ? (
                <div className="mt-1 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                  {onlyofficeWarningsByAssignmentId[assignmentId]}
                  {onRetryOnlyoffice ? (
                    <button
                      type="button"
                      className="ml-2 underline font-medium"
                      onClick={() => assignmentId && onRetryOnlyoffice(assignmentId)}
                    >
                      Neu versuchen erlauben
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2 justify-end items-center">
              {onArchiveSubmission ? (
                <button
                  type="button"
                  className={`h-9 w-9 rounded-full border text-[11px] font-semibold transition-colors ${
                    canArchiveSubmission
                      ? 'border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100'
                      : 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                  disabled={!canArchiveSubmission}
                  title={
                    !canArchiveSubmission
                      ? 'Nur bei vorhandener, aktueller Abgabe möglich'
                      : 'Abgabe archivieren und neue Einreichung erlauben'
                  }
                  onClick={() => assignmentId && canArchiveSubmission && onArchiveSubmission(assignmentId)}
                >
                  A
                </button>
              ) : null}
              <button
                type="button"
                className={`h-9 w-9 rounded-full border text-sm font-semibold transition-colors ${
                  canStartCorrection || isCorrectionRunning
                    ? 'border-indigo-400 bg-indigo-50 text-indigo-900 hover:bg-indigo-100'
                    : 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
                disabled={!canStartCorrection}
                title={
                  isCorrectionRunning
                    ? 'LLM-Korrektur läuft im Hintergrund'
                    : !latestSubmission
                    ? 'Keine Abgabe vorhanden'
                    : !canQueueCorrection
                      ? 'Erst nach Abgabe möglich'
                      : !analysisReady
                        ? 'Analyse läuft noch'
                        : 'Korrigieren (LLM-Lauf starten)'
                }
                onClick={() => assignmentId && canStartCorrection && onQueueCorrection(assignmentId)}
              >
                {isCorrectionRunning ? (
                  <span className="inline-block h-4 w-4 rounded-full border-2 border-indigo-300 border-t-indigo-800 animate-spin align-middle" />
                ) : (
                  'K'
                )}
              </button>
              {onViewCorrectionResult ? (
                <button
                  type="button"
                  className={`h-9 w-9 rounded-full border text-sm font-semibold transition-colors ${
                    canOpenKiModal
                      ? 'border-blue-400 bg-blue-50 text-blue-900 hover:bg-blue-100'
                      : 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                  disabled={!canOpenKiModal}
                  title={
                    !canOpenKiModal
                      ? isCorrectionRunning
                        ? 'Während Korrekturlauf gesperrt'
                        : !hasCorrectionResult
                          ? 'Erst verfügbar, wenn K vollständig fertig ist'
                          : 'Erst nach fertiger Analyse verfügbar'
                      : 'KI-Analyse öffnen (editierbar)'
                  }
                  onClick={() => assignmentId && canOpenKiModal && onViewCorrectionResult(assignmentId)}
                >
                  I
                </button>
              ) : null}
              {onOpenPipelineReview ? (
                <button
                  type="button"
                  className={`h-9 w-9 rounded-full border text-sm font-semibold transition-colors ${
                    analysisReady
                      ? 'border-teal-400 bg-teal-50 text-teal-900 hover:bg-teal-100'
                      : 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                  disabled={!analysisReady}
                  title={analysisReady ? 'Pipeline-Zerlegung prüfen (Regionen/Crops)' : 'Erst nach fertiger Analyse verfügbar'}
                  onClick={() => assignmentId && analysisReady && onOpenPipelineReview(assignmentId)}
                >
                  Ü
                </button>
              ) : null}
              <button
                type="button"
                className={`h-9 w-9 rounded-full border text-sm font-semibold transition-colors ${
                  canAssignGrade
                    ? isPublished
                      ? 'border-rose-400 bg-rose-50 text-rose-900 hover:bg-rose-100'
                      : 'border-emerald-400 bg-emerald-50 text-emerald-900 hover:bg-emerald-100'
                    : 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
                disabled={!canAssignGrade}
                title={!canAssignGrade ? 'Erst nach Bewertung möglich' : isPublished ? 'Veröffentlichung widerrufen' : 'Note vergeben / veröffentlichen'}
                onClick={() => assignmentId && canAssignGrade && onPublish(assignmentId, { revoke: isPublished })}
              >
                N
              </button>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}
